import type { HTMLAttributes, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { useDelayedUnmount, useWindowKeydown } from '@elfsight/lib-utils';
import { Backdrop } from '../backdrop';
import { Portal } from '../portal';
import { zIndexLayer } from '../zIndexLayer';

const SHEET_OPEN_ANIMATION_DURATION_MS = 300;
export const SHEET_CLOSE_ANIMATION_DURATION_MS = 100;
const CLOSING_CLASS = 'mobile-bottom-sheet-container-closing';

export type MobileBottomSheetProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    ariaLabel?: string;
    open: boolean;
    height?: string;
    backdropBackgroundColor?: string;
    closeOnBackdrop?: boolean;
    shadow?: boolean;
    onCloseRequest: () => void;
  }>;

export function MobileBottomSheet({
  ariaLabel,
  children,
  className,
  open,
  height = '50%',
  backdropBackgroundColor = 'rgba(17, 17, 17, 0.05)',
  closeOnBackdrop = true,
  shadow,
  onCloseRequest
}: MobileBottomSheetProps) {
  const [isMounted, isClosing] = useDelayedUnmount(
    open,
    SHEET_CLOSE_ANIMATION_DURATION_MS
  );
  const isVisible = open && !isClosing;

  useWindowKeydown('Escape', onCloseRequest);

  return (
    isMounted && (
      <Portal shadow={shadow}>
        <AnimatedBackdrop
          _preventPointerEvents={!closeOnBackdrop}
          background={backdropBackgroundColor}
          open={open}
        />

        <Container
          _height={height}
          className={isVisible ? className : `${className} ${CLOSING_CLASS}`}
          aria-label={ariaLabel}
          role="dialog"
          aria-modal="true"
        >
          {children}
        </Container>
      </Portal>
    )
  );
}

const AnimatedBackdrop = styled(Backdrop)<{ _preventPointerEvents?: boolean }>`
  ${({ _preventPointerEvents }) =>
    _preventPointerEvents &&
    css`
      pointer-events: none;
    `}
`;

const Container = styled.div<{
  _height: string;
}>`
  ${({ _height }) => css`
    position: fixed;
    inset: auto auto 0 0;
    z-index: ${zIndexLayer.modal};
    display: flex;
    width: 100%;
    height: ${_height};
    max-height: calc(100% - 40px);
    overflow: hidden;
    border-radius: 24px 24px 0 0;
    box-shadow:
      0px -31px 9px 0px rgba(0, 0, 0, 0),
      0px -20px 8px 0px rgba(0, 0, 0, 0),
      0px -11px 7px 0px rgba(0, 0, 0, 0.02),
      0px -5px 5px 0px rgba(0, 0, 0, 0.03),
      0px -1px 3px 0px rgba(0, 0, 0, 0.03);
    transition-property: opacity, transform;
    transition-duration: ${SHEET_OPEN_ANIMATION_DURATION_MS}ms;

    &.${CLOSING_CLASS} {
      opacity: 0;
      transform: translateY(10px);
      transition-duration: ${SHEET_CLOSE_ANIMATION_DURATION_MS}ms;
    }

    @starting-style {
      opacity: 0;
      transform: translateY(20px);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;
